import api from './api';

class PriceService {
  constructor() {
    this.cache = new Map();
    this.cacheTTL = 60 * 1000; // 60 seconds - match server update frequency
    this.lastFetchTime = null;
    this.lastServerUpdateTime = null;
    this.perCoinUpdateTimes = new Map();
    this.isRefreshing = false;
    
    // Add local cache for coins data to avoid repeated API calls
    this.coinsCache = null;
    this.coinsCacheTTL = 60 * 1000; // 1 minute
    this.lastCoinsFetchTime = null;
  }

  async fetchPrices(symbols, caller = 'unknown') {
    if (!symbols || symbols.length === 0) return {};
    
    // Log the request
    // console.log(`[PriceService] Request from ${caller}: ${symbols.length} symbols:`, symbols);
    
    const cacheKey = symbols.sort().join(',');
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
      // Log cache hit
      // console.log(`[PriceService] Cache hit for ${caller}: ${Object.keys(cached.data).length} coins (${Math.floor((Date.now() - cached.timestamp) / 1000)}s old)`);
      // console.log(`[PriceService] Cached prices:`, cached.data);
      return cached.data;
    }

    try {
      this.isRefreshing = true;
      
      // console.log(`[PriceService] Fetching prices from database for ${caller}: ${symbols.length} symbols...`);
      
      // Get all coins from database (includes prices) - using /api/coins instead of CoinGecko
      const response = await api.get('/api/coins');
      const allCoins = response.data;
      
      // console.log(`[PriceService] Database returned ${allCoins.length} total coins`);
      
      // Filter to only requested symbols and extract prices
      const prices = {};
      for (const symbol of symbols) {
        // Handle symbol mapping for database lookup (same as chart data mapping)
        let symbolToLookup = symbol;
        if (symbol === 'Bonk') {
          symbolToLookup = 'BONK';
        } else if (symbol === '$WIF') {
          symbolToLookup = 'WIF';
        }
        
        // Convert symbol to uppercase for database lookup (database stores symbols as uppercase)
        const upperSymbol = symbolToLookup.toUpperCase();
        const coin = allCoins.find(c => c.symbol === upperSymbol);
        if (coin && coin.current_price && parseFloat(coin.current_price) > 0) {
          prices[symbol] = { usd: parseFloat(coin.current_price) };
          // console.log(`[PriceService] ✅ Found price for ${symbol}: $${coin.current_price}`);
        } else {
          // console.log(`[PriceService] ❌ No price for ${symbol}:`, {
          //   coinFound: !!coin,
          //   currentPrice: coin?.current_price,
          //   priceValid: coin?.current_price && parseFloat(coin.current_price) > 0,
          //   lookedFor: upperSymbol
          // });
        }
      }
      
      const fetchTime = Date.now();
      this.lastFetchTime = fetchTime;
      
      // console.log(`[PriceService] Database response: ${Object.keys(prices).length}/${symbols.length} prices found`);
      // console.log(`[PriceService] Final prices object:`, prices);

      // Check for missing prices
      const missingPrices = symbols.filter(symbol => !prices[symbol]);
      if (missingPrices.length > 0) {
        // console.warn(`[PriceService] Missing prices for symbols: ${missingPrices.join(', ')}`);
        // console.log(`[PriceService] These coins may not have prices yet or may not exist in database`);
      }

      this.cache.set(cacheKey, {
        data: prices,
        timestamp: fetchTime
      });

      // console.log(`[PriceService] Database fetch completed for ${caller}: ${Object.keys(prices).length} coins`);

      return prices;
    } catch (error) {
      console.error('[PriceService] Error fetching prices from database:', error);
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  getLastFetchTime() {
    return this.lastFetchTime;
  }

  getLastServerUpdateTime() {
    return this.lastServerUpdateTime;
  }

  getPerCoinUpdateTime(coinId) {
    return this.perCoinUpdateTimes.get(coinId);
  }

  getPerCoinUpdateTimes() {
    return this.perCoinUpdateTimes;
  }

  async getFreshnessSummary(symbols) {
    if (!symbols || symbols.length === 0) {
      return { fresh: 0, stale: 0, oldestAge: 0, summary: 'No coins' };
    }

    // Get CoinGecko IDs for all symbols (now async)
    const coinIds = [];
    for (const symbol of symbols) {
      const coinId = await this.getCoinGeckoId(symbol);
      if (coinId) {
        coinIds.push(coinId);
      }
    }
    
    let freshCount = 0;
    let staleCount = 0;
    let oldestAge = 0;
    let batchNumbers = new Set();

    coinIds.forEach(coinId => {
      const updateInfo = this.perCoinUpdateTimes.get(coinId);
      if (updateInfo) {
        if (updateInfo.batchNumber) {
          batchNumbers.add(updateInfo.batchNumber);
        }
        if (updateInfo.ageSeconds <= 120) { // 2 minutes or less is fresh
          freshCount++;
        } else {
          staleCount++;
        }
        oldestAge = Math.max(oldestAge, updateInfo.ageSeconds || 0);
      } else {
        staleCount++; // No update info means stale
      }
    });

    const total = freshCount + staleCount;
    const oldestMinutes = Math.floor(oldestAge / 60);
    
    let summary = 'No data';
    if (total > 0) {
      if (batchNumbers.size > 1) {
        // Multiple batches were processed
        summary = `${freshCount}/${total} fresh (${batchNumbers.size} batches, ${oldestMinutes}m oldest)`;
      } else if (freshCount === total) {
        summary = `All ${total} fresh (${oldestMinutes}m old)`;
      } else if (staleCount === total) {
        summary = `All ${total} stale (${oldestMinutes}m old)`;
      } else {
        summary = `${freshCount}/${total} fresh (${oldestMinutes}m oldest)`;
      }
    }

    return {
      fresh: freshCount,
      stale: staleCount,
      oldestAge,
      summary,
      batchesProcessed: batchNumbers.size
    };
  }

  isRefreshingPrices() {
    return this.isRefreshing;
  }

  clearCache() {
    this.cache.clear();
    this.lastFetchTime = null;
    this.lastServerUpdateTime = null;
    this.perCoinUpdateTimes.clear();
    // Also clear the coins cache to force fresh data
    this.coinsCache = null;
    this.lastCoinsFetchTime = null;
    // console.log('[PriceService] All caches cleared');
  }

  // CoinGecko ID mapping - now with local caching
  async getCoinGeckoId(symbol) {
    try {
      const coins = await this.getCoinsData();
      const coin = coins.find(c => c.symbol === symbol);
      if (coin && coin.coingecko_id) {
        return coin.coingecko_id;
      }
    } catch (error) {
      console.warn(`[PriceService] Failed to get coingecko_id for ${symbol} from database:`, error.message);
    }
    return symbol.toLowerCase();
  }

  async getSymbolFromCoinGeckoId(coinId) {
    try {
      const coins = await this.getCoinsData();
      const coin = coins.find(c => c.coingecko_id === coinId);
      if (coin && coin.symbol) {
        return coin.symbol;
      }
    } catch (error) {
      console.warn(`[PriceService] Failed to get symbol for ${coinId} from database:`, error.message);
    }
    return coinId.toUpperCase();
  }

  // New method to get coins data with caching
  async getCoinsData() {
    // Check if we have valid cached data
    if (this.coinsCache && this.lastCoinsFetchTime && 
        Date.now() - this.lastCoinsFetchTime < this.coinsCacheTTL) {
      return this.coinsCache;
    }

    // Fetch fresh data
    try {
      // console.log(`[PriceService] Fetching coins data from /api/coins (cache expired or missing)`);
      const response = await api.get('/api/coins');
      this.coinsCache = response.data;
      this.lastCoinsFetchTime = Date.now();
      // console.log(`[PriceService] Cached ${this.coinsCache.length} coins for ${this.coinsCacheTTL/1000}s`);
      return this.coinsCache;
    } catch (error) {
      console.error(`[PriceService] Failed to fetch coins data:`, error.message);
      // Return cached data even if expired, or empty array if no cache
      return this.coinsCache || [];
    }
  }

  // Force refresh prices by clearing cache and fetching fresh data
  async forceRefreshPrices(symbols, caller = 'unknown') {
    // console.log(`[PriceService] Force refreshing prices for ${caller}`);
    this.clearCache();
    return await this.fetchPrices(symbols, caller);
  }
}

export default new PriceService(); 